// Citation display primitives — shared types. These mirror the wall-walled provenance backend
// (DF-4 context_graph + DF-2 sources): a context-graph binds a RETRIEVED chunk set and a CITED
// subset (cited ⊆ retrieved); each chunk belongs to a source artifact. The primitives are
// presentational (props in, no fetch) so any app can render cited content; data wiring lives in
// useProvenance + the mount.

export type ChunkRole = "cited" | "retrieved";

/** Mirror of backend GET /context-graphs/{id}/chunks → one chunk with its content + role. */
export interface ProvenanceChunk {
  chunk_id: string;
  source_id: string;
  ord: number;
  zone: string;
  role: ChunkRole;
  content: string;
  /** Row 19.7 (passage-span, source-side) — this chunk's own span within its parent source
   *  document. Already computed by the backend chunker; this response just started exposing it. */
  char_start: number;
  char_end: number;
}

/** Row 19.7 — a re-render-surviving span anchor: content-hash + offset, never DOM position.
 *  `start`/`end` are offsets into the CONTENT the anchor is resolved against (the specific
 *  content string is contextual — a chunk's `content` for `passageSpan`, the rendered answer
 *  text for `claimSpan`); `contentHash` lets a resolver detect drift before trusting the
 *  offsets. See `spanAnchor.ts` for the resolver. */
export interface SpanAnchor {
  start: number;
  end: number;
  contentHash: string;
}

/** The only source type this codebase's citation model produces today — a typed discriminator
 *  so the union has somewhere to grow without a breaking change (row 19.5). */
export type CitationSourceType = "source_artifact";

/** A normalized pointer to what a citation cites — row 19.5's `sourceRef`. */
export interface CitationSourceRef {
  sourceId: string;
  chunkId: string;
}

/** Subset of DF-2 SourceResponse needed to render the source a citation points to. */
export interface SourceLite {
  id: string;
  filename: string;
  zone: string;
  content_type: string;
  byte_size: number;
  pii_findings: number;
  quarantined: boolean;
  uploaded_by: string;
  created_at: string;
}

/** Row 19.5 — the one construction of a citation's stable id, shared by every caller that
 *  builds a CitationRef (useProvenance.ts, AiContextModule.tsx) so the format can never drift
 *  between them (found as a duplication note in post-build review — extracted here). */
export function buildCitationId(contextGraphId: string, chunkId: string): string {
  return `${contextGraphId}:${chunkId}`;
}

/** A numbered citation: the cited chunk + (optionally) its resolved source artifact. */
export interface CitationRef {
  index: number;
  chunk: ProvenanceChunk;
  source?: SourceLite;
  /** Row 19.5 — a stable identifier for this citation, computed from the context-graph +
   *  chunk it binds (not a separate backend column — no migration needed for this half). */
  citationId: string;
  sourceType: CitationSourceType;
  sourceRef: CitationSourceRef;
  /** Row 19.7 claim-side span (where in the GENERATED ANSWER this citation applies) — typed
   *  but never populated today: there is no persisted answer text and no live producer (the
   *  forward-half AI engine is scripted-deterministic, CFR-VW-026). A future real integration
   *  slots a value in here without a breaking change. */
  claimSpan?: SpanAnchor;
  /** Row 19.7 passage-span (source-side) — a sub-chunk anchor into `chunk.content`, resolved
   *  via `spanAnchor.ts`. Populated only where a caller supplies one (no live Anthropic
   *  Citations API producer yet — CFR); the resolver/contract itself is real and tested. */
  passageSpan?: SpanAnchor;
}

/** The retrieved→cited binding header (verdict/confidence + counts) for a context-graph. */
export interface LineageBinding {
  id: string;
  label?: string | null;
  kind: string;
  verdict?: string | null;
  confidence?: number | null;
  retrievedCount: number;
  citedCount: number;
}
